/* eslint-disable no-param-reassign */
import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { AddMediaPanel } from '@bingads-webui-campaign-react/asset-library-shared-add-media-layout';
import { StockImageDataService } from '@bingads-webui-campaign/stock-image-data-service';

export const AddImageWrapper = ({
  addMediaConfig,
  assetLibraryConfig,
  dataService,
  i18n,
  initialView,
  isAssetLibraryAIGCMVPEnabled,
  isOpen,
  logger,
  setNewImageDataObject,
  showAddImagePanel,
  setShowAddImagePanel,
  cid,
  aid,
  ...rest
}) => {
  const [addMediaPanelView, setAddMediaPanelView] = useState(initialView);

  const handleImageSelected = useCallback((imageId, imageUrl) => {
    if (imageUrl) {
      setNewImageDataObject({ url: imageUrl });
      setShowAddImagePanel(false);
      setAddMediaPanelView(initialView);
      return null;
    }
    return dataService.getImageById({ imageId }).then((imageData) => {
      setNewImageDataObject({ url: imageData.url, id: imageData.id }); // force to create a new object to trigger the re-render
      setShowAddImagePanel(false);
      setAddMediaPanelView(initialView);
    });
  }, [dataService, initialView, setNewImageDataObject, setShowAddImagePanel]);

  const onImageSelected = useCallback((image, onFinished) => {
    handleImageSelected(image.id, image.url, onFinished);
  }, [handleImageSelected]);

  const onImagesSelected = useCallback(imageIds => handleImageSelected(imageIds[0]), [handleImageSelected]);

  const processUploadedImage = useCallback(({
    imageId,
  }) => handleImageSelected(imageId), [handleImageSelected]);

  const { oDataConfig } = assetLibraryConfig;

  const { stockImageConfig } = rest;

  const stockImageService = useMemo(() => new StockImageDataService({
    cid, aid, oDataConfig, controller: 'StockImages',
  }), [oDataConfig, aid, cid]);

  const getStockImages = useCallback(options => stockImageService.getStockImages(options), [stockImageService]);

  const stockImageConfigInner = useMemo(() => ({
    getStockImages,
    onStockImageSelected: ({ selectedImageId }) => dataService.uploadStockImage({ stockImageId: selectedImageId }),
  }), [getStockImages, dataService]);

  assetLibraryConfig.onImageSelected = onImageSelected;
  assetLibraryConfig.onImagesSelected = onImagesSelected;
  assetLibraryConfig.enableImageDeleting = false;
  assetLibraryConfig.enableStockMultipleSelection = false;
  assetLibraryConfig.enableWebsiteMultipleSelection = false;

  return (
    <AddMediaPanel
      assetLibraryDataService={dataService}
      addMediaConfig={addMediaConfig}
      assetLibraryConfig={assetLibraryConfig}
      logger={logger}
      isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
      {...rest}
      initialView={initialView}
      isOpen={showAddImagePanel}
      setIsOpen={() => setShowAddImagePanel(false)}
      i18n={i18n}
      maxUploadNum={1}
      managedViewSelectionState={addMediaPanelView}
      updateManagedViewSelectionState={(newView) => { setAddMediaPanelView(newView); }}
      processUploadedImage={args => processUploadedImage(args)}
      stockImageConfig={stockImageConfig || stockImageConfigInner}
      cid={cid}
      aid={aid}
    />
  );
};

AddImageWrapper.propTypes = {
  addMediaConfig: PropTypes.shape({}).isRequired,
  assetLibraryConfig: PropTypes.shape({
    oDataConfig: PropTypes.shape({}),
  }).isRequired,
  dataService: PropTypes.shape({
    getImageById: PropTypes.func,
    uploadStockImage: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  initialView: PropTypes.string.isRequired,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  logger: PropTypes.shape({}).isRequired,
  setNewImageDataObject: PropTypes.func.isRequired,
  showAddImagePanel: PropTypes.bool.isRequired,
  setShowAddImagePanel: PropTypes.func.isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
};

AddImageWrapper.defaultProps = {
  isAssetLibraryAIGCMVPEnabled: false,
};
